import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import type { TaskInstance } from 'ember-concurrency';
import { t } from 'ember-intl';
import SettingsPmsIntegrationSeasonalRoomHierarchyModal from 'lumen/components/settings/pms-integration/seasonal-room-hierarchy-modal';
import type { RecommendationStrategy } from 'lumen/models';
import type { RoomOffsetHierarchySettings } from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { sortSeasonalStrategies } from 'lumen/utils/pricing-assistant/recommendation-settings';

interface SettingsPmsIntegrationSeasonalRoomHierarchySectionSignature {
  Args: {};
}

export default class SettingsPmsIntegrationSeasonalRoomHierarchySection extends Component<SettingsPmsIntegrationSeasonalRoomHierarchySectionSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service declare store: Services['store'];

  /*
   * The default room_offset_hierarchy strategy returned alongside the seasonal
   * ones; null until the request resolves.
   */
  @tracked defaultStrategy: RecommendationStrategy | null = null;
  // The seasonal strategy currently open in the editor; null shows the list.
  @tracked editingSeasonalStrategy: RecommendationStrategy | null = null;
  @tracked isModalOpen = false;
  @tracked seasonalStrategies: RecommendationStrategy[] = [];
  @tracked strategiesTask: TaskInstance<
    RecommendationStrategy[] | null
  > | null = null;

  /*
   * The hotel's default room offsets, taken from the default strategy in the
   * response. Seeds new strategies and backs Reset-to-Default. Null only while
   * the request is in flight (the modal shows a spinner).
   */
  get defaultRoomOffsetHierarchySettings(): RoomOffsetHierarchySettings | null {
    const settings = this.defaultStrategy?.settings;

    return settings?.settings_type === 'room_offset_hierarchy'
      ? settings
      : null;
  }

  get isFeatureEnabled(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PRICING_MANAGER_SEASONAL_ROOM_HIERARCHY,
    );
  }

  get isLoading(): boolean {
    return this.strategiesTask?.isRunning ?? false;
  }

  get isReadOnly(): boolean {
    return this.access.isPricingAssistantPmsSettingsActionOrInputDisabled;
  }

  // Auto-generated name for a new one: the next free "Seasonal strategy N".
  private get nextSeasonalStrategyName(): string {
    const prefix = this.intl.t(
      'pricing_assistant.recommendations.seasonal_strategy',
      { count: '' },
    );
    const pattern = new RegExp(`^${prefix}(\\d+)`);

    const maxNumber = this.seasonalStrategies.reduce((max, strategy) => {
      const captured = strategy.name.match(pattern)?.[1];
      return Math.max(max, captured ? parseInt(captured, 10) : 0);
    }, 0);

    return this.intl.t('pricing_assistant.recommendations.seasonal_strategy', {
      count: maxNumber + 1,
    });
  }

  @action
  closeModal(): void {
    // Closing tears down the editor, whose willDestroy discards unsaved changes
    // (incl. removing an unsaved new one) — no lifecycle flag to reset here.
    this.isModalOpen = false;
    this.editingSeasonalStrategy = null;
  }

  @action
  async deleteSeasonalStrategy(
    seasonalStrategy: RecommendationStrategy,
  ): Promise<void> {
    const isConfirmed = await this.alerting.showWarningAlert({
      title: this.intl.t(
        'pricing_assistant.recommendations.delete_seasonal_strategy',
      ),
      text: this.intl.t(
        'pricing_assistant.recommendations.delete_seasonal_strategy_text',
        { strategy: seasonalStrategy.name },
      ),
      dangerMode: true,
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: { text: this.intl.t('general.delete') },
      },
    });

    if (!isConfirmed) {
      return;
    }

    try {
      await seasonalStrategy.destroyRecord();
      this.seasonalStrategies = this.seasonalStrategies.filter(
        (strategy) => strategy !== seasonalStrategy,
      );
    } catch {
      // Restore the record so the card reappears, then surface the failure.
      seasonalStrategy.rollbackAttributes();
      await this.alerting.showErrorAlert();
    }
  }

  @action
  onCreateSeasonalStrategy(): void {
    // Seed the new one from the hotel default offsets (deep-copied so it owns its
    // settings object); the user then refines the per-room offsets in the editor.
    const seasonalStrategy = this.store.createRecord<RecommendationStrategy>(
      'recommendation-strategy',
      {
        name: this.nextSeasonalStrategyName,
        seasonStartDt: null,
        seasonEndDt: null,
        conditions: null,
        settings: JSON.parse(
          JSON.stringify(this.defaultRoomOffsetHierarchySettings),
        ) as RecommendationStrategy['settings'],
        settingsType: 'room_offset_hierarchy',
        subscriptionId: Number(this.hotelAndMarketService.hotel.id),
      },
    );

    // Show it in the list (sorted to the top) and open it for editing.
    this.seasonalStrategies = sortSeasonalStrategies([
      ...this.seasonalStrategies,
      seasonalStrategy,
    ]);
    this.editingSeasonalStrategy = seasonalStrategy;
  }

  @action
  onEditorCancel(): void {
    const seasonalStrategy = this.editingSeasonalStrategy;
    this.editingSeasonalStrategy = null;

    // A brand-new seasonal strategy only lived in the list optimistically; the
    // card is about to unload it, so drop it here while isNew is still readable.
    if (seasonalStrategy?.isNew) {
      this.seasonalStrategies = this.seasonalStrategies.filter(
        (strategy) => strategy !== seasonalStrategy,
      );
    }
  }

  @action
  onEditSeasonalStrategy(seasonalStrategy: RecommendationStrategy): void {
    this.editingSeasonalStrategy = seasonalStrategy;
  }

  @action
  onSeasonalStrategySaved(seasonalStrategy: RecommendationStrategy): void {
    const seasonalStrategies = this.seasonalStrategies.includes(
      seasonalStrategy,
    )
      ? this.seasonalStrategies
      : [...this.seasonalStrategies, seasonalStrategy];

    // Re-sort in case a new one was added or an existing range changed.
    // sortSeasonalStrategies filters into a fresh array, so no copy is needed.
    this.seasonalStrategies = sortSeasonalStrategies(seasonalStrategies);
    this.editingSeasonalStrategy = null;
  }

  @action
  async openModal(): Promise<void> {
    this.isModalOpen = true;
    this.strategiesTask =
      this.pricingAssistantDataRequestService.recommendationsStrategiesTask.perform(
        'room_offset_hierarchy',
      );
    const strategies = (await this.strategiesTask) ?? [];

    // The default strategy backs new-strategy seeding and Reset-to-Default;
    // sortSeasonalStrategies filters it out of the list the editor shows.
    this.defaultStrategy =
      strategies.find((strategy) => strategy.isDefaultStrategy) ?? null;

    this.seasonalStrategies = sortSeasonalStrategies(strategies);
  }

  <template>
    {{#if this.isFeatureEnabled}}
      <div class="divider-line" data-test-id="seasonal-room-hierarchy-divider">
      </div>

      <div
        class="flex flex-row items-center justify-between"
        data-test-id="seasonal-room-hierarchy-section"
      >
        <p class="text-200--bold">{{t
            "pricing_assistant.recommendations.price_range.subtitle.seasonal_strategies"
          }}</p>

        <PrismButton
          @buttonType="secondary"
          @label={{if this.isReadOnly (t "general.view") (t "general.edit")}}
          @onClick={{this.openModal}}
        />
      </div>

      {{#if this.isModalOpen}}
        <SettingsPmsIntegrationSeasonalRoomHierarchyModal
          @defaultSettings={{this.defaultRoomOffsetHierarchySettings}}
          @editingSeasonalStrategy={{this.editingSeasonalStrategy}}
          @isLoading={{this.isLoading}}
          @isReadOnly={{this.isReadOnly}}
          @onClose={{this.closeModal}}
          @onCreateSeasonalStrategy={{this.onCreateSeasonalStrategy}}
          @onDeleteSeasonalStrategy={{this.deleteSeasonalStrategy}}
          @onEditSeasonalStrategy={{this.onEditSeasonalStrategy}}
          @onEditorCancel={{this.onEditorCancel}}
          @onSeasonalStrategySaved={{this.onSeasonalStrategySaved}}
          @seasonalStrategies={{this.seasonalStrategies}}
        />
      {{/if}}
    {{/if}}
  </template>
}
